import { useCallback, useEffect, useState } from "react";
import { CalendarCheck, DownloadSimple, Warning, CheckCircle, Users, Baby, Chalkboard } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  adminDaySnapshot,
  listRecordsForRange,
  markAttendance,
  rowsToCsv,
  STATUS_META,
  todayStr,
  formatTime,
} from "@/lib/attendance";
import AdminAttendanceNoticesStrip from "@/components/attendance/AdminAttendanceNoticesStrip";

const HEALTH_META = {
  healthy: { label: "Healthy",   tone: "border-primary/30 bg-primary/10 text-primary-hover",  icon: CheckCircle },
  near:    { label: "Near limit",tone: "border-amber-300 bg-amber-50 text-amber-700",         icon: Warning },
  over:    { label: "Over ratio",tone: "border-[#C6851E]/50 bg-[#C6851E]/10 text-[#C6851E]",  icon: Warning },
  none:    { label: "No ratio",  tone: "border-neutral-200 bg-neutral-100 text-neutral-500",  icon: CheckCircle },
};

export default function AdminAttendance() {
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState({ classrooms: [] });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportRange, setExportRange] = useState({ from: todayStr(), to: todayStr() });
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await adminDaySnapshot(date);
      setData(snap);
    } catch (err) {
      toast.error(err.message || "Could not load attendance");
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const totals = data.classrooms.reduce(
    (a, r) => ({
      enrolled: a.enrolled + r.active_children_count,
      present: a.present + (r.counts.present || 0) + (r.counts.late || 0),
      absent: a.absent + (r.counts.absent || 0) + (r.counts.excused || 0),
      unmarked: a.unmarked + (r.counts.unmarked || 0),
      overRatio: a.overRatio + (r.health === "over" ? 1 : 0),
    }),
    { enrolled: 0, present: 0, absent: 0, unmarked: 0, overRatio: 0 },
  );

  const onOverride = async (childId, status) => {
    try {
      await markAttendance({ childId, date, status });
      toast.success(`Updated to ${STATUS_META[status]?.label || status}`);
      await load();
    } catch (err) {
      toast.error(err.message || "Could not update");
    }
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const rows = await listRecordsForRange({
        from: exportRange.from,
        to: exportRange.to,
      });
      if (rows.length === 0) {
        toast.error("No attendance records in that range");
        return;
      }
      const csv = rowsToCsv(rows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance_${exportRange.from}_to_${exportRange.to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} records`);
    } catch (err) {
      toast.error(err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="admin-attendance" className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Operational
          </p>
          <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
            Attendance
          </h1>
          <p className="mt-2 max-w-2xl text-neutral-600">
            Live view of who's in today, ratio health per classroom, and
            CSV exports for any date range.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CalendarCheck size={18} weight="duotone" className="text-primary-hover" />
          <label htmlFor="admin-attendance-date" className="sr-only">Attendance date</label>
          <input
            id="admin-attendance-date"
            data-testid="admin-attendance-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <button
            type="button"
            data-testid="admin-attendance-today"
            onClick={() => setDate(todayStr())}
            className="rounded-full border border-neutral-200 bg-white px-3 py-2 text-xs font-semibold text-neutral-600 hover:bg-neutral-50"
          >
            Today
          </button>
        </div>
      </header>

      {/* KPIs */}
      <section className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Kpi testId="admin-attendance-kpi-enrolled"  label="Enrolled" value={totals.enrolled} />
        <Kpi testId="admin-attendance-kpi-present"   label="Present"  value={totals.present} accent />
        <Kpi testId="admin-attendance-kpi-absent"    label="Absent"   value={totals.absent} />
        <Kpi testId="admin-attendance-kpi-unmarked"  label="Unmarked" value={totals.unmarked} />
        <Kpi testId="admin-attendance-kpi-over"      label="Over ratio" value={totals.overRatio} danger={totals.overRatio > 0} />
      </section>

      <AdminAttendanceNoticesStrip date={date} />

      {/* CSV export */}
      <section className="rounded-2xl border border-neutral-200 bg-white p-5">
        <p className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
          <DownloadSimple size={12} weight="duotone" />
          Export attendance (CSV)
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="mb-1 block text-[11px] text-neutral-500">From</span>
            <input
              data-testid="admin-attendance-export-from"
              type="date"
              value={exportRange.from}
              onChange={(e) => setExportRange({ ...exportRange, from: e.target.value })}
              className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] text-neutral-500">To</span>
            <input
              data-testid="admin-attendance-export-to"
              type="date"
              value={exportRange.to}
              onChange={(e) => setExportRange({ ...exportRange, to: e.target.value })}
              className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </label>
          <button
            type="button"
            data-testid="admin-attendance-export"
            disabled={exporting}
            onClick={onExport}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
          >
            <DownloadSimple size={12} weight="duotone" />
            {exporting ? "Exporting…" : "Download CSV"}
          </button>
        </div>
      </section>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        </div>
      ) : data.classrooms.length === 0 ? (
        <div data-testid="admin-attendance-empty" className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center">
          <p className="font-heading text-base font-semibold text-neutral-900">No classrooms yet</p>
          <p className="mx-auto mt-2 max-w-sm text-sm text-neutral-500">
            Create classrooms from the Classrooms page to start tracking attendance.
          </p>
        </div>
      ) : (
        <ul data-testid="admin-attendance-classrooms" className="space-y-3">
          {data.classrooms.map((r) => {
            const health = HEALTH_META[r.health] || HEALTH_META.none;
            const HealthIcon = health.icon;
            const maxChildren = r.ratio && r.staff_count > 0 ? r.staff_count * r.ratio : null;
            const isOpen = expanded === r.id;
            return (
              <li
                key={r.id}
                data-testid={`admin-attendance-classroom-${r.id}`}
                data-health={r.health}
                className="overflow-hidden rounded-2xl border border-neutral-200 bg-white"
              >
                <button
                  type="button"
                  data-testid={`admin-attendance-classroom-${r.id}-toggle`}
                  onClick={() => setExpanded(isOpen ? null : r.id)}
                  className="flex w-full flex-wrap items-center justify-between gap-3 px-5 py-4 text-left hover:bg-neutral-50"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-secondary/30">
                      <Chalkboard size={16} weight="duotone" className="text-primary-hover" />
                    </span>
                    <div className="min-w-0">
                      <p className="font-heading text-base font-semibold tracking-tight text-neutral-900">
                        {r.name}
                      </p>
                      <p className="truncate text-xs text-neutral-500">
                        {r.age_group || "—"}
                        {r.capacity ? ` · Capacity ${r.capacity}` : ""}
                        {r.ratio ? ` · 1:${r.ratio} ratio` : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <CountBlock icon={<Baby size={12} weight="duotone" />} label="Present" value={r.effective_present} />
                    <CountBlock icon={<Users size={12} weight="duotone" />} label="Staff" value={r.staff_count} />
                    {r.ratio && maxChildren != null && (
                      <CountBlock label="Max" value={`${r.effective_present} / ${maxChildren}`} />
                    )}
                    <span
                      data-testid={`admin-attendance-classroom-${r.id}-health`}
                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${health.tone}`}
                    >
                      <HealthIcon size={10} weight="duotone" />
                      {health.label}
                    </span>
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-neutral-100 px-5 py-4">
                    <ul className="space-y-2" data-testid={`admin-attendance-classroom-${r.id}-rows`}>
                      {r.rows.length === 0 ? (
                        <li className="text-xs text-neutral-500">No active children in this classroom.</li>
                      ) : (
                        r.rows.map((row) => {
                          const s = row.attendance?.status || "unmarked";
                          const meta = STATUS_META[s];
                          return (
                            <li
                              key={row.child.id}
                              data-testid={`admin-attendance-row-${row.child.id}`}
                              data-status={s}
                              className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-neutral-100 bg-neutral-50 p-2.5"
                            >
                              <div className="flex min-w-0 items-center gap-2">
                                <span className="truncate text-sm font-medium text-neutral-900">
                                  {row.child.first_name} {row.child.last_name || ""}
                                </span>
                                <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.tone}`}>
                                  {meta.label}
                                </span>
                                {row.attendance?.check_in_at && (
                                  <span className="text-[11px] text-neutral-500">
                                    In {formatTime(row.attendance.check_in_at)}
                                  </span>
                                )}
                                {row.attendance?.check_out_at && (
                                  <span
                                    className="text-[11px] text-neutral-500"
                                    data-testid={`admin-attendance-row-${row.child.id}-pickup`}
                                  >
                                    · Out {formatTime(row.attendance.check_out_at)}
                                    {row.attendance?.pickup_authorization?.full_name
                                      ? ` · by ${row.attendance.pickup_authorization.full_name}`
                                      : row.attendance?.pickup_self
                                        ? " · by parent"
                                        : ""}
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-1">
                                {["present", "late", "absent", "excused"].map((opt) => (
                                  <button
                                    key={opt}
                                    type="button"
                                    data-testid={`admin-attendance-row-${row.child.id}-${opt}`}
                                    onClick={() => onOverride(row.child.id, opt)}
                                    aria-label={STATUS_META[opt].label}
                                    className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-all ${
                                      s === opt
                                        ? "border-primary/50 bg-primary/15 text-primary-hover"
                                        : "border-neutral-200 bg-white text-neutral-500 hover:bg-white hover:text-neutral-900"
                                    }`}
                                  >
                                    {STATUS_META[opt].label}
                                  </button>
                                ))}
                              </div>
                            </li>
                          );
                        })
                      )}
                    </ul>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Kpi({ label, value, accent, danger, testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border bg-white p-4 ${danger ? "border-[#C6851E]/40" : accent ? "border-primary/30" : "border-neutral-200"}`}
    >
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </p>
      <p className={`mt-1 font-heading text-2xl font-semibold tracking-tight ${danger ? "text-[#C6851E]" : accent ? "text-primary-hover" : "text-neutral-900"}`}>
        {value}
      </p>
    </div>
  );
}

function CountBlock({ icon, label, value }) {
  return (
    <div className="flex items-center gap-1.5 text-sm">
      {icon && <span className="text-neutral-400">{icon}</span>}
      <span className="font-mono font-semibold text-neutral-900">{value}</span>
      <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{label}</span>
    </div>
  );
}
